import { f0FormField, RenderIfCondition } from '@factorialco/f0-react'
import { F0SelectItemObject } from '@factorialco/f0-react'
import { z } from 'zod'

import { TeamRecord, useTeamSelectorSource } from './source'

type BaseTeamSelectorFormFieldParams = {
  label: string
  section?: string
  placeholder?: string
  helpText?: string
  renderIf?: RenderIfCondition
  row?: string
}

const mapTeamOptions = (team: TeamRecord): F0SelectItemObject<string, TeamRecord> => ({
  value: team.id,
  label: team.name,
  item: team,
})

// Single select - optional
const useOptionalSingleTeamSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
}: BaseTeamSelectorFormFieldParams) => {
  const schema = z.string().optional()

  const source = useTeamSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    showSearchBox: true,
    source,
    mapOptions: mapTeamOptions,
  })
}

// Single select - mandatory
const useMandatorySingleTeamSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
}: BaseTeamSelectorFormFieldParams) => {
  const schema = z.string().min(1)

  const source = useTeamSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    showSearchBox: true,
    source,
    mapOptions: mapTeamOptions,
  })
}

// Multi select - optional
const useOptionalMultiTeamSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
}: BaseTeamSelectorFormFieldParams) => {
  const schema = z.array(z.string()).optional()

  const source = useTeamSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    multiple: true,
    showSearchBox: true,
    source,
    mapOptions: mapTeamOptions,
  })
}

// Multi select - mandatory
const useMandatoryMultiTeamSelectorFormField = ({
  label,
  section,
  placeholder,
  helpText,
  renderIf,
  row,
}: BaseTeamSelectorFormFieldParams) => {
  const schema = z.array(z.string()).min(1)

  const source = useTeamSelectorSource()

  return f0FormField(schema, {
    label,
    section,
    fieldType: 'select',
    helpText,
    placeholder,
    renderIf,
    row,
    multiple: true,
    showSearchBox: true,
    source,
    mapOptions: mapTeamOptions,
  })
}

type SingleTeamSelectorFormFieldParams<M extends boolean> = BaseTeamSelectorFormFieldParams & {
  mandatory: M
}

export const useSingleTeamSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: SingleTeamSelectorFormFieldParams<M>) => {
  const mandatorySingleTeamSelectorFormField = useMandatorySingleTeamSelectorFormField({
    ...rest,
    renderIf,
  })
  const optionalSingleTeamSelectorFormField = useOptionalSingleTeamSelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory ? mandatorySingleTeamSelectorFormField : optionalSingleTeamSelectorFormField
}

type MultiTeamSelectorFormFieldParams<M extends boolean> = BaseTeamSelectorFormFieldParams & {
  mandatory: M
}

export const useMultiTeamSelectorFormField = <M extends boolean>({
  mandatory,
  renderIf,
  ...rest
}: MultiTeamSelectorFormFieldParams<M>) => {
  const mandatoryMultiTeamSelectorFormField = useMandatoryMultiTeamSelectorFormField({
    ...rest,
    renderIf,
  })
  const optionalMultiTeamSelectorFormField = useOptionalMultiTeamSelectorFormField({
    ...rest,
    renderIf,
  })

  return mandatory ? mandatoryMultiTeamSelectorFormField : optionalMultiTeamSelectorFormField
}
